import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { EmptyStateComponent } from '../../components/empty-state/empty-state.component';
import { SkeletonComponent } from '../../components/skeleton/skeleton.component';
import { SessionModel } from '../../models/session.model';
import { DateToMomentPipe } from '../../pipes/date-to-moment.pipe';
import { FieldTypeService } from '../../services/field-type.service';
import { SessionService } from '../../services/session.service';
import { TemplateService } from '../../services/template.service';
import { UserService } from '../../services/user.service';

/** Identificadores de las cuatro metricas de la portada. */
type KpiKey = 'users' | 'sessions' | 'templates' | 'fieldTypes';

interface IKpi {
  key: KpiKey;
  label: string;
  icon: string;
  /**
   * Sufijo de la clase `.ks-kpi-card--*`.
   *
   * `brand` (el rojo del sistema) queda reservado a la métrica principal: solo
   * una tarjeta puede llevarlo. Las otras tres se reparten matices lejanos
   * entre sí y del rojo —cian, violeta y verde— para que la barrita de color
   * siga siendo distinguible; y como el color nunca es el único indicador,
   * cada tarjeta lleva además icono y etiqueta.
   */
  color: 'brand' | 'blue-2' | 'purple' | 'green';
  hint: string;
  /** `null` = la metrica fallo; se pinta un guion. */
  value: number | null;
  loading: boolean;
  /**
   * Ruta de `app.routes.ts` a la que navega la tarjeta entera. Cada KPI tiene
   * su propio módulo (incluida "Sesiones activas": sería inconsistente dejarla
   * sin destino cuando las otras tres sí lo tienen), así que el destino no
   * depende de que el dato haya cargado — vive fijo aquí, no se calcula.
   */
  route: string;
}

/** Cuantas sesiones recientes se listan bajo los KPIs. */
const RECENT_SESSIONS = 10;

/** Tamano de pagina del listado de usuarios que alimenta el mapa uid → nombre. */
const USERS_SAMPLE = 100;

/**
 * Portada de la consola.
 *
 * El API NO expone un endpoint de metricas: cada KPI se compone con el `total`
 * (o la longitud) que ya devuelven los listados existentes. Cada carga es
 * independiente: si una falla, esa tarjeta muestra un guion y el resto de la
 * pagina sigue en pie.
 */
@Component({
  selector: 'app-dashboard-page',
  imports: [RouterLink, DateToMomentPipe, SkeletonComponent, EmptyStateComponent],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPageComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly sessionService = inject(SessionService);
  private readonly templateService = inject(TemplateService);
  private readonly fieldTypeService = inject(FieldTypeService);

  protected readonly kpis = signal<IKpi[]>([
    {
      key: 'users',
      label: 'Usuarios',
      icon: 'people',
      color: 'brand',
      // El desglose por origen (Firebase Auth / base de datos) ya no vive
      // aquí: tiene su propia sección con cifras y gráfica más abajo
      // (`migrationSummary`). Repetirlo en el hint sería contar la misma
      // historia dos veces; esta tarjeta solo necesita decir qué mide el
      // número grande.
      hint: 'Total de cuentas',
      value: null,
      loading: true,
      route: '/dashboard/users',
    },
    {
      key: 'sessions',
      label: 'Sesiones activas',
      icon: 'broadcast',
      color: 'blue-2',
      hint: 'Dispositivos con sesión abierta',
      value: null,
      loading: true,
      route: '/dashboard/sessions',
    },
    {
      key: 'templates',
      label: 'Plantillas',
      icon: 'file-earmark-text',
      color: 'purple',
      hint: 'Formatos de la bóveda',
      value: null,
      loading: true,
      route: '/dashboard/catalogues/templates',
    },
    {
      key: 'fieldTypes',
      label: 'Tipos de campo',
      icon: 'ui-checks-grid',
      color: 'green',
      hint: 'Catálogo de tipos',
      value: null,
      loading: true,
      route: '/dashboard/catalogues/field-types',
    },
  ]);

  /**
   * Avance de la migracion de Firebase Auth a base de datos. Se alimenta del
   * `summary` que ya trae la MISMA llamada a `GET /users` con la que se arma
   * el KPI "Usuarios" (ver `loadUsers`); no hay una petición aparte. `null`
   * mientras carga o si esa llamada falló — igual que el resto de los KPIs,
   * para no enseñar un 0 % que en realidad es "no se sabe".
   */
  protected readonly migrationSummary = this.userService.usersSummary;

  /**
   * Avance de aceptación de Términos y condiciones + Aviso de privacidad,
   * de los usuarios YA REGISTRADOS. Misma fuente que `migrationSummary`
   * (`summary` de la MISMA llamada a `GET /users`, sin petición aparte):
   * ver el comentario de `IUsersSummary.acceptedBoth` en `user.service.ts`
   * para el porqué ese campo todavía es una propuesta, no un dato confirmado
   * del API. `null` mientras el API no lo mande — el panel usa el mismo
   * estado "sin datos disponibles" que el resto de los KPIs.
   */
  protected readonly legalSummary = computed<{ registered: number; acceptedBoth: number } | null>(
    () => {
      const summary = this.userService.usersSummary();
      if (!summary || summary.acceptedBoth == null) return null;
      return { registered: summary.registered, acceptedBoth: summary.acceptedBoth };
    },
  );

  protected readonly sessions = signal<SessionModel[]>([]);

  /**
   * Arranca en `true`: la carga se dispara en `ngOnInit`, asi que el primer
   * render debe pintar esqueletos, nunca el estado vacio.
   */
  protected readonly loadingSessions = signal(true);

  /**
   * Mensaje del fallo de carga, o `null`. Distingue «no se pudo cargar» de
   * «todavia no hay sesiones»: no son lo mismo y el usuario necesita saber cual
   * es. OJO: los servicios NO propagan la excepcion (la atrapan y avisan por
   * toast), asi que el fallo se lee de su senal de error, no de un `catch`.
   */
  protected readonly sessionsError = signal<string | null>(null);

  /** uid → nombre completo, para no enseñar identificadores crudos. */
  private readonly userNames = signal<Record<string, string>>({});

  protected readonly hasSessions = computed(() => this.sessions().length > 0);

  /** Cuantas sesiones recientes se listan (para el texto de la cabecera). */
  protected readonly recentCount = RECENT_SESSIONS;

  /** Filas fantasma mientras carga la tabla. */
  protected readonly skeletonRows = Array.from({ length: 5 }, (_, index) => index);

  /**
   * Comparte el estado de carga del KPI "Usuarios": se resuelven juntos.
   * También gatea el panel de aceptación legal (`legalSummary`), que sale de
   * la misma llamada a `GET /users`.
   */
  protected readonly migrationLoading = computed(
    () => this.kpis().find((kpi) => kpi.key === 'users')?.loading ?? true,
  );

  ngOnInit(): void {
    void this.load();
  }

  /**
   * Nombre accesible de la tarjeta KPI, ahora que la tarjeta entera es el
   * enlace de navegación. NO se deja que lo componga el algoritmo por defecto
   * (concatenar el texto visible en orden de DOM: etiqueta, luego valor,
   * luego pie): eso dependería del orden visual y, con el valor todavía
   * cargando, anunciaría solo "Usuarios" o un guion suelto sin decir de qué
   * cifra se trata ni a dónde lleva. Se explicita todo en un único
   * `aria-label`, coherente en los tres estados (cargando / error / con
   * dato).
   */
  protected kpiAriaLabel(kpi: IKpi): string {
    if (kpi.loading) return `${kpi.label}: cargando. Ir a ${kpi.label}.`;
    if (kpi.value === null) return `${kpi.label}: sin datos disponibles. Ir a ${kpi.label}.`;
    return `${kpi.label}: ${kpi.value}. ${kpi.hint}. Ir a ${kpi.label}.`;
  }

  protected userName(uid: string): string {
    return this.userNames()[uid] ?? '—';
  }

  /** Etiqueta legible del dispositivo de una sesion. */
  protected deviceLabel(session: SessionModel): string {
    return (
      session.deviceName ||
      session.deviceDescription ||
      [session.brand, session.model].filter(Boolean).join(' ') ||
      'Dispositivo desconocido'
    );
  }

  protected platformIcon(session: SessionModel): string {
    const platform = (session.device?.platform ?? '').toLowerCase();
    if (platform.includes('ios') || platform.includes('apple')) return 'apple';
    if (platform.includes('android')) return 'android2';
    if (platform.includes('web')) return 'globe2';
    return 'phone';
  }

  /**
   * Radio del anillo SVG de migración (`viewBox="0 0 84 84"`, centro en
   * 42,42). Constante compartida por la plantilla (atributo `r`) y por el
   * cálculo del `stroke-dasharray`, para que nunca se desincronicen.
   */
  protected readonly migrationRingRadius = 34;

  /** Perímetro del anillo: base de todos los cálculos de `stroke-dasharray`. */
  private readonly migrationRingCircumference = 2 * Math.PI * this.migrationRingRadius;

  /**
   * Fracción mínima del anillo que se pinta como "hecho" o como "pendiente"
   * cuando esa parte no es cero. Con 1 de 820 migradas (0,12 %) el arco real
   * mediría menos de un pixel de perímetro (~0,26) y el anillo se vería
   * vacío, como si la gráfica no hubiera cargado. Fijar un mínimo del 3,5 %
   * (un arco de ~7,5, similar al grosor del trazo) lo hace perceptible sin
   * tocar el texto: el porcentaje mostrado siempre es el real, nunca el
   * forzado. Mismo criterio en el extremo contrario (819 de 820): el
   * pendiente conserva un arco visible en vez de leerse como "100 % listo".
   */
  private readonly migrationRingMinFraction = 0.035;

  /** Porcentaje exacto migrado, sin redondear. Sin división entre cero. */
  private migrationRawPercent(summary: { total: number; registered: number }): number {
    return summary.total > 0 ? (summary.registered / summary.total) * 100 : 0;
  }

  /**
   * Texto del porcentaje migrado. El redondeo a entero no debe mentir en los
   * extremos: con 1 de 820 (0,12 %) un simple `Math.round` da "0 %", que se
   * lee como "no hay ninguno" cuando sí hay uno. Con 819 de 820 pasaría lo
   * mismo al revés: "100 %" leería "ya terminó" cuando falta uno. Los casos
   * limpios (0 % real o 100 % real) sí se muestran tal cual — ahí el
   * redondeo no falsea nada.
   */
  protected migrationPercentLabel(summary: { total: number; registered: number }): string {
    const raw = this.migrationRawPercent(summary);
    const rounded = Math.round(raw);
    if (summary.registered > 0 && rounded <= 0) return '<1 %';
    if (summary.registered < summary.total && rounded >= 100) return '>99 %';
    return `${rounded} %`;
  }

  /** `aria-valuenow` del indicador de progreso: un decimal, tan honesto como el texto. */
  protected migrationPercentValue(summary: { total: number; registered: number }): number {
    return Math.round(this.migrationRawPercent(summary) * 10) / 10;
  }

  /**
   * `stroke-dasharray` del arco "migrado" del anillo SVG: `<pintado> <resto>`.
   * Aplica el mínimo perceptible de `migrationRingMinFraction` en ambos
   * extremos (ver su doc) sin alterar el porcentaje mostrado en texto.
   */
  protected migrationRingDashArray(summary: { total: number; registered: number }): string {
    const c = this.migrationRingCircumference;
    if (summary.total <= 0) return `0 ${c}`;

    let fraction = summary.registered / summary.total;
    if (summary.registered > 0) {
      fraction = Math.max(fraction, this.migrationRingMinFraction);
    }
    if (summary.registered < summary.total) {
      fraction = Math.min(fraction, 1 - this.migrationRingMinFraction);
    }

    const filled = fraction * c;
    return `${filled.toFixed(2)} ${(c - filled).toFixed(2)}`;
  }

  /** Texto accesible del indicador: el número, no solo el color, cuenta la historia. */
  protected migrationAriaLabel(summary: {
    total: number;
    registered: number;
    unregistered: number;
  }): string {
    return (
      `Migración a base de datos: ${summary.registered} de ${summary.total} cuentas ya ` +
      `migradas (${this.migrationPercentLabel(summary)}); ${summary.unregistered} pendientes.`
    );
  }

  // ---------------------------------------------------------------------------
  // Anillo de aceptación legal. Mismo mecanismo SVG y el mismo `<circle r>`
  // que el anillo de migración de arriba (`migrationRingRadius`,
  // `migrationRingCircumference`, `migrationRingMinFraction` se reutilizan tal
  // cual: es el MISMO tamaño de anillo, no hace falta una segunda constante).
  // Solo cambia la base del porcentaje: `registered` en vez de `total`, y
  // `acceptedBoth` en vez de `registered`.
  // ---------------------------------------------------------------------------

  /** Porcentaje exacto de aceptación, sin redondear. Sin división entre cero. */
  private legalRawPercent(summary: { registered: number; acceptedBoth: number }): number {
    return summary.registered > 0 ? (summary.acceptedBoth / summary.registered) * 100 : 0;
  }

  /** Mismo criterio de honestidad en los extremos que `migrationPercentLabel`. */
  protected legalPercentLabel(summary: { registered: number; acceptedBoth: number }): string {
    const raw = this.legalRawPercent(summary);
    const rounded = Math.round(raw);
    if (summary.acceptedBoth > 0 && rounded <= 0) return '<1 %';
    if (summary.acceptedBoth < summary.registered && rounded >= 100) return '>99 %';
    return `${rounded} %`;
  }

  /** `aria-valuenow` del indicador de progreso. */
  protected legalPercentValue(summary: { registered: number; acceptedBoth: number }): number {
    return Math.round(this.legalRawPercent(summary) * 10) / 10;
  }

  /** `stroke-dasharray` del arco "aceptó": mismo mínimo perceptible que la migración. */
  protected legalRingDashArray(summary: { registered: number; acceptedBoth: number }): string {
    const c = this.migrationRingCircumference;
    if (summary.registered <= 0) return `0 ${c}`;

    let fraction = summary.acceptedBoth / summary.registered;
    if (summary.acceptedBoth > 0) {
      fraction = Math.max(fraction, this.migrationRingMinFraction);
    }
    if (summary.acceptedBoth < summary.registered) {
      fraction = Math.min(fraction, 1 - this.migrationRingMinFraction);
    }

    const filled = fraction * c;
    return `${filled.toFixed(2)} ${(c - filled).toFixed(2)}`;
  }

  /** Texto accesible del indicador de aceptación legal. */
  protected legalAriaLabel(summary: { registered: number; acceptedBoth: number }): string {
    const pending = summary.registered - summary.acceptedBoth;
    return (
      `Aceptación legal: ${summary.acceptedBoth} de ${summary.registered} cuentas registradas ` +
      `ya aceptaron los Términos y condiciones y el Aviso de privacidad ` +
      `(${this.legalPercentLabel(summary)}); ${pending} con algo pendiente.`
    );
  }

  private async load(): Promise<void> {
    await Promise.all([
      this.loadUsers(),
      this.loadTemplates(),
      this.loadFieldTypes(),
      // Las dos llamadas a /sessions van encadenadas: comparten el `total` del
      // servicio y en paralelo se pisarian.
      this.loadSessions(),
    ]);
  }

  /** Reintento manual desde el estado de error de la tabla. */
  protected async retrySessions(): Promise<void> {
    this.loadingSessions.set(true);
    this.sessionsError.set(null);
    await this.loadSessions();
  }

  private async loadUsers(): Promise<void> {
    try {
      // page_size 100 en vez de 1: el `total` sigue siendo el mismo y de paso
      // resuelve los nombres de los usuarios de la tabla de sesiones. La misma
      // respuesta trae `summary` (ver `migrationSummary`), asi que UNA sola
      // llamada alimenta el KPI, el mapa de nombres Y el avance de migracion.
      const users = await this.userService.getUsers({ page: 1, pageSize: USERS_SAMPLE });
      this.userNames.set(
        users.reduce<Record<string, string>>((map, user) => {
          map[user.uid] = user.fullName;
          return map;
        }, {}),
      );
      // Un fallo no lanza: se lee de la senal de error del servicio. Sin esto
      // un API caido pintaria un 0, que se confunde con «no hay ninguno».
      this.setKpi('users', this.userService.usersError() ? null : this.userService.total());
    } catch {
      this.setKpi('users', null);
    }
  }

  private async loadTemplates(): Promise<void> {
    try {
      const templates = await this.templateService.getTemplates();
      this.setKpi('templates', this.templateService.templatesError() ? null : templates.length);
    } catch {
      this.setKpi('templates', null);
    }
  }

  private async loadFieldTypes(): Promise<void> {
    try {
      const fieldTypes = await this.fieldTypeService.getFieldTypes();
      this.setKpi(
        'fieldTypes',
        this.fieldTypeService.fieldTypesError() ? null : fieldTypes.length,
      );
    } catch {
      this.setKpi('fieldTypes', null);
    }
  }

  private async loadSessions(): Promise<void> {
    try {
      await this.sessionService.getSessions({ active: true, page: 1, pageSize: 1 });
      this.setKpi(
        'sessions',
        this.sessionService.sessionsError() ? null : this.sessionService.total(),
      );
    } catch {
      this.setKpi('sessions', null);
    }

    try {
      const recent = await this.sessionService.getSessions({
        page: 1,
        pageSize: RECENT_SESSIONS,
      });
      this.sessionsError.set(this.sessionService.sessionsError());
      this.sessions.set(this.sessionService.sessionsError() ? [] : recent);
    } catch {
      this.sessions.set([]);
      this.sessionsError.set('No se pudieron cargar las sesiones más recientes.');
    } finally {
      this.loadingSessions.set(false);
    }
  }

  private setKpi(key: KpiKey, value: number | null): void {
    this.kpis.update((kpis) =>
      kpis.map((kpi) => (kpi.key === key ? { ...kpi, value, loading: false } : kpi)),
    );
  }
}
